var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import styled from 'styled-components';
import { rem } from '../utils/Polished';
import { withForwardedRef } from '../utils/helpers/reactHelpers';
import Box from '../Box';
import Typography from '../Typography';
const StyledFakeRadio = styled.div `
  display: inline-block;
  vertical-align: middle;
  box-sizing: border-box;
  width: ${(p) => p.theme.SizeWidthRadioBase};
  height: ${(p) => p.theme.SizeHeightRadioBase};
  overflow: hidden;
  border-width: ${rem('1px')};
  border-style: solid;
  border-color: ${(p) => p.theme.ColorBorderRadioBase};
  border-radius: 50%;
  background-color: transparent;

  & > svg {
    margin-top: ${rem('-3.5px')};
    margin-left: ${rem('-3px')};
  }

  ._ndsRadio--checked & {
    position: relative;
    border-color: ${(p) => p.theme.ColorBorderRadioChecked};
    background-color: ${(p) => p.theme.ColorBackgroundRadioChecked};
  }

  ._ndsRadio--checked &::before {
    content: '';
    display: block;
    width: ${(p) => p.theme.SizeWidthRadioCheckedCircle};
    height: ${(p) => p.theme.SizeHeightRadioCheckedCircle};
    background-color: ${(p) => p.theme.ColorBackgroundRadioCheckedCircle};
    border-radius: 50%;
    position: absolute;
    top: ${rem('5px')};
    left: ${rem('5px')};
  }

  ._ndsRadio--checked._ndsRadio--disabled & {
    background-color: ${(p) => p.theme.ColorBackgroundRadioCheckedDisabled};
  }

  ._ndsRadio--disabled & {
    border-color: ${(p) => p.theme.ColorBorderRadioDisabled};
    background-color: transparent;
  }

  will-change: border-color, background-color, box-shadow;
  transition: border-color 0.3s, background-color 0.3s, box-shadow 0.3s;
`;
const RealHiddenInput = styled.input.attrs((props) => (Object.assign({ type: 'radio' }, props))) `
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: ${rem('1px')};
  margin: ${rem('-1px')};
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: ${rem('1px')};
`;
const WrapperLabel = styled.label `
  user-select: none;

  display: inline-flex;
  align-items: center;
  height: ${rem('10px')};
  line-height: ${rem('10px')};

  & > span {
    will-change: color;
    transition: color 0.3s;
  }

  &._ndsRadio--disabled > span {
    color: ${(p) => p.theme.ColorFontRadioDisabled};
  }

  :hover {
    cursor: pointer;
  }

  &._ndsRadio--disabled:hover {
    cursor: not-allowed;
  }

  & input:focus + ._ndsRadio__styled,
  :hover ._ndsRadio__styled {
    border-color: ${(p) => p.theme.ColorBorderRadioHover};
    box-shadow: ${(p) => p.theme.SizeShadowRadio}
      ${(p) => p.theme.ColorShadowRadioHover};
    background-color: transparent;
  }

  &._ndsRadio--checked input:focus + ._ndsRadio__styled,
  &._ndsRadio--checked:hover ._ndsRadio__styled {
    border-color: ${(p) => p.theme.ColorBorderRadioCheckedHover};
    background-color: ${(p) => p.theme.ColorBackgroundRadioCheckedHover};
  }

  &._ndsRadio--checked._ndsRadio--disabled input:focus + ._ndsRadio__styled,
  &._ndsRadio--checked._ndsRadio--disabled:hover ._ndsRadio__styled {
    border-color: ${(p) => p.theme.ColorBorderRadioDisabled};
    background-color: ${(p) => p.theme.ColorBackgroundRadioCheckedDisabled};
  }

  &._ndsRadio--disabled:hover ._ndsRadio__styled {
    border-color: ${(p) => p.theme.ColorBorderRadioDisabledHover};
    box-shadow: none;
  }
`;
const Radio = (props) => {
    const { disabled, checked: checkedProp, label, onChange, forwardedRef, className, id, name, value } = props, rest = __rest(props, ["disabled", "checked", "label", "onChange", "forwardedRef", "className", "id", "name", "value"]);
    const [checkedState, setCheckedState] = React.useState(Boolean(checkedProp));
    const { current: isControlled } = React.useRef(checkedProp !== undefined);
    const handleRadioChange = React.useCallback((event) => {
        const { target } = event;
        if (!isControlled && !disabled) {
            setCheckedState(target.checked);
        }
        if (onChange && !disabled) {
            onChange(event, target.checked);
        }
    }, [onChange, disabled, isControlled]);
    const handleKeyPress = React.useCallback((event) => {
        event.target.click();
    }, []);
    const checked = isControlled ? checkedProp : checkedState;
    return (React.createElement(WrapperLabel, { onKeyPress: handleKeyPress, className: clsx({ '_ndsRadio--checked': checked }, { '_ndsRadio--disabled': disabled }, className) },
        React.createElement(RealHiddenInput, Object.assign({ ref: forwardedRef, checked: checked, onChange: handleRadioChange, id: id, name: name, value: value }, rest)),
        React.createElement(StyledFakeRadio, { className: "_ndsRadio__styled" }),
        React.createElement(Box, { ml: 0.75 },
            React.createElement(Typography, { variant: "body3" }, label))));
};
Radio.propTypes = {
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    onChange: PropTypes.func,
    forwardedRef: PropTypes.any,
    id: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string
};
Radio.defaultProps = {
    checked: undefined,
    disabled: false,
    label: null,
    onChange: null,
    forwardedRef: null,
    id: undefined,
    name: undefined,
    value: undefined,
    className: undefined
};
export { Radio as RadioRootComp };
export default withForwardedRef(Radio);
//# sourceMappingURL=Radio.js.map